import { Injectable } from '@angular/core'
import * as _ from 'lodash'

import {
  normalizeFromZip,
  normalizeFromState,
  deduplicateAreas,
} from '@dataunveil/ducharts'

import { ViewModeConfig } from './config'
import { FilterService } from 'src/app/services/filter.service'
import { firstValueFrom } from 'rxjs'

// ─── Helpers ────────────────────────────────────────────────

function buildColorCode(colorTypes: any[]): Record<string, string> {
  return colorTypes.reduce((acc: any, curr: any) => {
    const key = Object.keys(curr)[0]
    acc[key] = curr[key]
    return acc
  }, {})
}

// ─── Service ────────────────────────────────────────────────

@Injectable()
export class TerritoryMapService {

  topology: any
  cities: any
  fipsByZip: any

  private dataCache: Map<string, any[]> = new Map()

  // ─── Load Static Assets (topology + zip-fips) ──────────

  async loadStaticData(): Promise<void> {
    const [topology, cities, fipsByZip] = await Promise.all([
      fetch('/assets/data/counties-10m.json').then(r => r.json()),      
      fetch('/assets/data/us.cities.json').then(r => r.json()),
      fetch('/assets/data/zip-fips.json').then(r => r.json()),
    ])
    this.topology = topology
    this.cities = cities
    this.fipsByZip = fipsByZip
  }

  // ─── Load Data for a Config via API ────────────────────

  async loadDataForConfig(cfg: ViewModeConfig, filterService: FilterService): Promise<any[]> {
    if (!cfg.apiKey) return []

    // Return cached data if already loaded for this apiKey
    if (this.dataCache.has(cfg.apiKey)) {
      return this.dataCache.get(cfg.apiKey)!
    }

    const obs = filterService.executeQuery({ api_key: cfg.apiKey })
    if (!obs) return []

    const response: any = await firstValueFrom(obs)
    const data = Array.isArray(response) ? response : response?.data ?? []

    this.dataCache.set(cfg.apiKey, data)
    return data
  }

  // ─── Clear cached data (e.g. on filter change) ────────

  clearCache() {
    this.dataCache.clear()
  }

  // ─── Build Data for Config ─────────────────────────────
  //
  // Dispatches to the correct formatter based on cfg.mode.

  buildDataForConfig(cfg: ViewModeConfig, sourceData: any[]): any {
    if (!sourceData?.length) return { data: [] }

    switch (cfg.mode) {
      case 'default':
        return { data: this.formatDefault(cfg, sourceData) }

      case 'territory-group':
        return { data: this.formatTerritoryGroup(cfg, sourceData) }

      case 'state-badge':
        return this.formatStateBadge(cfg, sourceData)

      case 'state-cone':
        return this.formatStateCone(cfg, sourceData)
      
      case 'state-icon':
        return this.formatStateIcon(cfg, sourceData)

      case 'state-bubble':
        return this.formatStateBubble(cfg, sourceData)
    }
  }

  // ─── Default: one pin per group ──────────────────────

  private formatDefault(cfg: ViewModeConfig, sourceData: any[]): any[] {
    return normalizeFromZip(sourceData, this.fipsByZip, {
      groupBy: cfg.groupBy,
      label: r => cfg.labelFields.map(f => `${r[f][0]}.`).join(' '),
      data: cfg.dataFields,
    })
  }

  // ─── State Badge: one badge per state ─────────────────

  private formatStateBadge(cfg: ViewModeConfig, sourceData: any[]): { data: any[]; min: number; max: number } {
    const valueField = cfg.valueField!

    if (sourceData?.length > 0) {
      const stateField = cfg.dataFields[0] || 'state'
      const sumField   = cfg.dataFields[1] || 'total_patients'
      const socField   = cfg.dataFields[2] || 'no_soc'

      const grouped = _.groupBy(sourceData, (m: any) => m[stateField])

      sourceData = Object.entries(grouped).map(([stateKey, rows]: [string, any[]]) => {
        const totalValue = rows.reduce((sum, r) => sum + (Number(r[sumField]) || 0), 0)
        const totalSoc   = rows.reduce((sum, r) => sum + (Number(r[socField]) || 0), 0)
        const first      = rows[0] || {}

        return {
          ...first,
          [stateField]:   stateKey,
          [sumField]:     totalValue,
          [socField]:     totalSoc,
          state:          stateKey,
          total_patients: totalValue,
          no_soc:         totalSoc,
          grouped:        rows,
        }
      })
    }

    const result = normalizeFromState(sourceData, this.topology, {
      label: r => r[cfg.labelFields[0]] || '',
      data: r => {
        const payload: any = {}
        for (const field of cfg.dataFields) {
          payload[field] = r[field] ?? null
        }
        payload.value = Number(r[valueField]) || 0
        payload.total_sites = Number(r.no_soc ?? 0)
        payload.grouped = r.grouped ?? []
        return payload
      },
    })

    const items = result.items.map(item => ({
      ...item,
      state: item.label,
      value: (item.data as any)?.value ?? 0,
      total_sites: (item.data as any)?.total_sites ?? 0,
      grouped: (item.data as any)?.grouped ?? [],
    }))

    return { data: items, min: result.min, max: result.max }
  }

  // ─── State Cone: one cone per site-of-care ────────────

  private formatStateCone(cfg: ViewModeConfig, sourceData: any[]): { data: any[]; min?: number; max?: number } {
    const valueField = cfg.valueField!
    const colorField = cfg.colorField!
    const flagField = cfg.flagField!
    const colorCode = buildColorCode(cfg.colorTypes)

    const values = sourceData.map((s: any) => Number(s[valueField]) || 0)
    const min = Math.min(...values)
    const max = Math.max(...values)

    const items = normalizeFromZip(sourceData, this.fipsByZip, {
      groupBy: cfg.groupBy,
      label: r => `${r[cfg.labelFields[0]]}.`,
      data: (r, _existing) => ({
        ...r,
        value: Number(r[valueField]) || 0,
        color: colorCode[r[colorField]],
        flag: r[flagField],
      }),
    })

    items.forEach((item: any) => {
      item.min = min
      item.max = max
    })

    return { data: deduplicateAreas(items), min, max }
  }

  // ─── State Icon: one icon per site-of-care (color from value) ─

  private formatStateIcon(cfg: ViewModeConfig, sourceData: any[]): { data: any[]; min?: number; max?: number } {
    const valueField = cfg.valueField!
    const flagField = cfg.flagField!
    const zipField = cfg.zipField || 'zip_5'

    // Pre-group by cfg.groupBy so each aggregated row retains its underlying rows
    // (exposed as `grouped`) and shares a single summed `value`.
    if (sourceData?.length > 0 && cfg.groupBy) {
      const groupKeys = Array.isArray(cfg.groupBy) ? cfg.groupBy : [cfg.groupBy]
      const grouped = _.groupBy(sourceData, (m: any) =>
        groupKeys.map((k: string) => m[k]).join('||')
      )

      sourceData = Object.values(grouped).map((rows: any[]) => {
        const totalValue = rows.reduce((sum, r) => sum + (Number(r[valueField]) || 0), 0)
        const first = rows[0] || {}
        return {
          ...first,
          [valueField]: totalValue,
          grouped:      rows,
        }
      })
    }

    const values = sourceData.map((s: any) => Number(s[valueField]) || 0)
    const min = values.length ? Math.min(...values) : 0
    const max = values.length ? Math.max(...values) : 0

    const items = normalizeFromZip(sourceData, this.fipsByZip, {
      groupBy: cfg.groupBy,
      zipField,
      label: r => `${r[cfg.labelFields[0]]}.`,
      data: (r, _existing) => ({
        ...r,
        city:              r["city"],
        state:             r['state_cd'],
        zip_5:             r[zipField],
        value: Number(r[valueField]) || 0,
        flag: r[flagField],
        grouped: r.grouped ?? [],
      }),
    })

    items.forEach((item: any) => {
      item.min = min
      item.max = max
      item.value = item.data?.value ?? 0
    })

    return { data: deduplicateAreas(items), min, max }
  }
  
  // ─── Territory Group: employee pins + hierarchy sub-pins ─

  private formatTerritoryGroup(cfg: ViewModeConfig, sourceData: any[]): any[] {
    const mainData = normalizeFromZip(sourceData, this.fipsByZip, {
      groupBy: cfg.groupBy,
      label: r => cfg.labelFields.map(f => `${r[f][0]}.`).join(' '),
      data: r => ({ ...r }),
    })

    if (!cfg.hierarchy?.length) return mainData

    const allGroups = this.buildHierarchyGroups(sourceData, cfg.hierarchy)

    allGroups.forEach((group: any) => {
      const items = normalizeFromZip(group.data, this.fipsByZip, {
        groupBy: 'employee_key',
        label: () => '',
        data: r => ({ ...r }),
      })
      const current = items[0]
      if (current?.areas?.length) {
        group.areas = current.areas
        group.label = ''
        group.data = { ...group, group_level: group.group_level }
      }
    })

    deduplicateAreas(allGroups)

    return [...mainData, ...allGroups]
  }

  // ─── Hierarchy Group Builder ──────────────────────────

  private buildHierarchyGroups(data: any[], hierarchyConfig: any[]): any[] {
    const allGroups: any[] = []

    for (const cfg of hierarchyConfig) {
      const { keys, level, labelKey, prescriberKeys } = cfg

      const grouped = data.reduce((acc: any, item: any) => {
        if (keys.some((k: string) => item[k] == null)) return acc
        const key = keys.map((k: string) => item[k]).join('-')
        if (!acc[key]) acc[key] = []
        acc[key].push(item)
        return acc
      }, {})

      const groups = Object.entries(grouped).map(([key, value]: [string, any[]] | any) => {
        const firstItem = value[0]
        const prescriber_data = _.uniqBy(
          value.map((v: any) => ({
            [prescriberKeys.id]: v[prescriberKeys.id],
            [prescriberKeys.name]: v[prescriberKeys.name],
          })),
          prescriberKeys.id,
        )

        const obj: any = {
          id: key,
          group_level: level,
          data: value,
          key: labelKey,
          prescriber_data,
        }
        keys.forEach((k: string) => (obj[k] = firstItem[k]))
        return obj
      })

      allGroups.push(...groups)
    }

    return allGroups
  }

  // ─── State Bubble: one bubble per site-of-care (color by practice_type) ─

  private formatStateBubble(cfg: ViewModeConfig, sourceData: any[]): { data: any[]; min?: number; max?: number } {
    const valueField = cfg.valueField!
    const cityField    = cfg.cityField    || 'city'
    const stateField   = cfg.stateField   || 'state'
    const zipField     = cfg.zipField     || 'zip_5'
    const addressField = cfg.addressField || 'address'
    const idField      = cfg.idField      || 'oracle_account_id'
    const siteField    = cfg.siteField    || 'site_of_care'

    const values = sourceData.map((s: any) => Number(s[valueField]) || 0)
    const min = values.length ? Math.min(...values) : 0
    const max = values.length ? Math.max(...values) : 0

    const items = normalizeFromZip(sourceData, this.fipsByZip, {
      groupBy: cfg.groupBy,
      zipField,
      label: r => `${r[cfg.labelFields[0]]}`,
      data: (r, _existing) => ({
        ...r,
        city:              r[cityField],
        state:             r[stateField],
        zip_5:             r[zipField],
        address:           r[addressField],
        oracle_account_id: r[idField],
        site_of_care:      r[siteField],
        value: Number(r[valueField]) || 0,
      }),
    })

    return { data: deduplicateAreas(items), min, max }
  }
}
